import React from 'react';
import { Box, Flex, HStack, VStack, Text, Heading, Button, Image } from '@chakra-ui/react';
import { FiMapPin, FiCalendar, FiUser, FiShield } from 'react-icons/fi';
import { EVENT_FORMATS } from './eventConstants';
import { TYPE_META, PREVIEW_FALLBACK } from './venueTypeMeta';
import { parseCoverImage } from '../../services/api';

const PreviewRow = ({ icon, children, muted }) => (
  <HStack spacing={2.5} align="center">
    <Box flexShrink={0} color="rgba(255,255,255,0.4)" mt="1px">{icon}</Box>
    <Text fontSize="sm" color={muted ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.72)'} noOfLines={1}>
      {children}
    </Text>
  </HStack>
);

const Chip = ({ children, accent }) => (
  <Box
    px={2.5}
    py="3px"
    borderRadius="full"
    bg="rgba(255,255,255,0.05)"
    border={`1px solid ${accent ? accent.replace('0.95', '0.28') : 'rgba(255,255,255,0.12)'}`}
  >
    <Text fontSize="11px" fontWeight="600" color={accent || 'rgba(255,255,255,0.7)'} letterSpacing="0.01em">
      {children}
    </Text>
  </Box>
);

const formatStart = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * The sleek dark event card.
 *  - mode="review"  → in-wizard / compliance preview (shows pending state).
 *  - mode="invite"  → the finalized card invitees actually receive.
 *
 * `data` shape: { title, summary, format, category, expectedAttendees,
 *                 visibility, venueName, city, venueType, start }
 */
const EventPreviewCard = ({ data = {}, mode = 'review' }) => {
  const isInvite = mode === 'invite';
  const meta = TYPE_META[data.venueType] || PREVIEW_FALLBACK;
  const Watermark = meta.icon;
  const formatLabel = (EVENT_FORMATS.find((f) => f.value === data.format) || {}).label || 'In person';
  const title = (data.title || '').trim();
  const summary = (data.summary || '').trim();
  const location = data.venueName || (data.city ? data.city.split(',')[0] : null);
  const isPrivate = data.visibility === 'private';
  const startLabel = formatStart(data.start);
  const isPaid = data.pricingType === 'paid';
  const priceLabel = isPaid
    ? data.ticketPrice
      ? `$${data.ticketPrice} per person`
      : 'Paid'
    : 'Free';
  const cover = data.coverImage ? parseCoverImage(data.coverImage) : null;

  return (
    <Box
      w="100%"
      maxW="380px"
      mx="auto"
      bgGradient="linear(180deg, #161618, #0b0b0c)"
      borderRadius="22px"
      overflow="hidden"
      border="1px solid rgba(255,255,255,0.08)"
      boxShadow="0 30px 90px rgba(0,0,0,0.65)"
    >
      {/* Cover — 2:1, uniform with the card, form cover box, and focal modal */}
      <Box position="relative" overflow="hidden" sx={{ aspectRatio: '2 / 1' }}>
        {cover ? (
          <Image src={cover.src} alt="" w="100%" h="100%" objectFit="cover" objectPosition={cover.objectPosition} />
        ) : (
          <>
            <Box position="absolute" inset={0} bgGradient={meta.gradient} opacity={0.92} />
            <Flex position="absolute" inset={0} align="center" justify="center" opacity={0.16}>
              <Watermark size={66} color="#ffffff" />
            </Flex>
          </>
        )}
        <Box position="absolute" inset={0} bgGradient="linear(180deg, rgba(0,0,0,0) 35%, #0b0b0c)" />
        <Flex position="absolute" top={0} left={0} right={0} p={4} justify="space-between" align="flex-start">
          <Box px={2.5} py="3px" borderRadius="full" bg="rgba(0,0,0,0.35)" backdropFilter="blur(6px)" border="1px solid rgba(255,255,255,0.15)">
            <Text fontSize="11px" fontWeight="600" color="#fafafa" letterSpacing="0.02em">
              {isPrivate ? 'Private event' : 'Public event'}
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Body */}
      <Box px={5} pt={1} pb={isInvite ? 4 : 5}>
        <Heading
          size="md"
          color={title ? '#fafafa' : 'rgba(255,255,255,0.3)'}
          fontWeight="700"
          letterSpacing="-0.01em"
          lineHeight="1.2"
          mb={3}
          noOfLines={2}
        >
          {title || 'Untitled event'}
        </Heading>

        <HStack spacing={2} mb={4}>
          <Flex w="26px" h="26px" borderRadius="full" bgGradient={meta.gradient} align="center" justify="center" color="rgba(255,255,255,0.85)">
            <FiUser size={13} />
          </Flex>
          <Text fontSize="sm" color="rgba(255,255,255,0.6)">
            Hosted by <Text as="span" color="#fafafa" fontWeight="600">Your firm</Text>
          </Text>
        </HStack>

        <VStack align="stretch" spacing={2.5} mb={5}>
          <PreviewRow icon={<FiMapPin size={15} />} muted={!location}>
            {location || 'Venue to be selected'}
          </PreviewRow>
          <PreviewRow icon={<FiCalendar size={15} />} muted={!startLabel}>
            {startLabel || (isInvite ? 'Date to be announced' : 'Date set after approval')}
          </PreviewRow>
        </VStack>

        <Box borderTop="1px solid rgba(255,255,255,0.07)" pt={4}>
          <Text fontSize="10px" fontWeight="700" color="rgba(255,255,255,0.35)" letterSpacing="0.1em" textTransform="uppercase" mb={2}>
            Overview
          </Text>
          <Text fontSize="sm" color={summary ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)'} lineHeight="1.65" mb={4}>
            {summary || 'Add a summary to give attendees a snapshot of your event.'}
          </Text>

          <HStack spacing={2} flexWrap="wrap" sx={{ rowGap: '8px' }}>
            {data.category && <Chip>{data.category}</Chip>}
            <Chip accent={meta.tint}>{formatLabel}</Chip>
            {data.expectedAttendees && <Chip>Up to {data.expectedAttendees} guests</Chip>}
            <Chip accent={isPaid ? undefined : 'rgba(122,226,161,0.95)'}>{priceLabel}</Chip>
          </HStack>
        </Box>
      </Box>

      {/* Invite footer — the RSVP affordance attendees see */}
      {isInvite && (
        <Flex align="center" justify="space-between" px={5} py={4} bg="rgba(255,255,255,0.02)" borderTop="1px solid rgba(255,255,255,0.07)">
          <Box>
            <Text fontSize="11px" color="rgba(255,255,255,0.4)">Registration</Text>
            <Text fontSize="sm" fontWeight="600" color="#fafafa">RSVP to reserve a seat</Text>
          </Box>
          <Button size="sm" h="34px" bg="#fafafa" color="#0a0a0a" borderRadius="full" fontWeight="600" fontSize="sm" _hover={{ opacity: 0.85 }} _focus={{ boxShadow: 'none' }}>
            RSVP
          </Button>
        </Flex>
      )}
    </Box>
  );
};

// Standalone status banner — intentionally separate from the card so it reads
// as app chrome, not part of the attendee-facing event page.
export const ComplianceBanner = () => (
  <Flex
    align="center"
    gap={3}
    w="100%"
    maxW="380px"
    mx="auto"
    mt={4}
    px={4}
    py={3.5}
    borderRadius="14px"
    bg="rgba(251,191,36,0.07)"
    border="1px solid rgba(251,191,36,0.2)"
  >
    <Flex w="32px" h="32px" borderRadius="10px" bg="rgba(251,191,36,0.12)" border="1px solid rgba(251,191,36,0.28)" align="center" justify="center" color="rgba(251,191,36,0.9)" flexShrink={0}>
      <FiShield size={15} />
    </Flex>
    <Box>
      <Text fontSize="sm" fontWeight="600" color="rgba(251,191,36,0.95)" lineHeight="1.3">Awaiting compliance review</Text>
      <Text fontSize="xs" color="rgba(255,255,255,0.45)" lineHeight="1.4">Promotion &amp; scheduling unlock once approved.</Text>
    </Box>
  </Flex>
);

export default EventPreviewCard;
